import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { CurrentUser } from '@/types/api';
import defaultSettings from '../config/defaultSettings';
import { getCurrentUserInfo, isLoggedIn, clearToken } from './utils/token';
import { extractUserMenuCodes } from './utils/menuPermission';
import { buildFileUrl } from './utils/download';
import { readUserNeedChangePassword } from './constants/auth';

const DEFAULT_AVATAR = require('./assets/user/robot-avatar.png');

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: CurrentUser;
  userMenuCodes?: string[];
  loading?: boolean;
  fetchUserInfo?: () => Promise<CurrentUser | undefined>;
  routes?: any[];
  mustChangeInitialPassword?: boolean;
}> {
  const defaultUser: CurrentUser = {
    name: '开发用户',
    avatar: DEFAULT_AVATAR,
    userid: 'dev001',
    email: 'dev@example.com',
    signature: '开发环境',
    title: '开发者',
    group: '开发团队',
    tags: [],
    notifyCount: 0,
    unreadCount: 0,
    country: 'China',
    access: 'admin',
    geographic: {
      province: { label: '浙江省', key: '330000' },
      city: { label: '杭州市', key: '330100' },
    },
    address: '开发地址',
    phone: '000-00000000',
  };

  const fetchUserInfo = async () => {
    try {
      const userDetail = await getCurrentUserInfo();

      if (userDetail) {
        const currentUser: CurrentUser & { roles?: any[] } = {
          name: userDetail.cname || userDetail.username,
          avatar: userDetail.avatar ? buildFileUrl(userDetail.avatar) : DEFAULT_AVATAR,
          userid: userDetail.id?.toString(),
          email: userDetail.email,
          phone: userDetail.phone,
          access: 'admin',
          title: userDetail.description,
          group: userDetail.depts?.[0]?.name || '默认部门',
          tags:
            userDetail.roles?.map((role: any) => ({
              key: role.id?.toString(),
              label: role.name,
            })) || [],
          notifyCount: 0,
          unreadCount: 0,
          employeeNumber: userDetail.employeeNumber,
          country: 'China',
          geographic: {
            province: { label: '浙江省', key: '330000' },
            city: { label: '杭州市', key: '330100' },
          },
          address: userDetail.description || '开发地址',
          roles: userDetail.roles,
        };
        return currentUser;
      }
      return defaultUser;
    } catch (error) {
      console.error('获取用户信息失败:', error);
      return defaultUser;
    }
  };

  try {
    if (isLoggedIn()) {
      try {
        const userDetail = await getCurrentUserInfo();

        if (userDetail) {
          const currentUser: CurrentUser & { roles?: any[] } = {
            name: userDetail.cname || userDetail.username,
            avatar: userDetail.avatar ? buildFileUrl(userDetail.avatar) : DEFAULT_AVATAR,
            userid: userDetail.id?.toString(),
            email: userDetail.email,
            phone: userDetail.phone,
            access: 'admin',
            title: userDetail.description,
            group: userDetail.depts?.[0]?.name || '默认部门',
            tags:
              userDetail.roles?.map((role: any) => ({
                key: role.id?.toString(),
                label: role.name,
              })) || [],
            notifyCount: 0,
            unreadCount: 0,
            employeeNumber: userDetail.employeeNumber,
            country: 'China',
            geographic: {
              province: { label: '浙江省', key: '330000' },
              city: { label: '杭州市', key: '330100' },
            },
            address: userDetail.description || '开发地址',
            roles: userDetail.roles,
          };

          const userMenuCodes = extractUserMenuCodes(userDetail);

          return {
            fetchUserInfo,
            currentUser,
            userMenuCodes,
            mustChangeInitialPassword: readUserNeedChangePassword(userDetail),
            settings: defaultSettings as Partial<LayoutSettings>,
          };
        }
      } catch (error) {
        console.error('获取用户信息失败:', error);
        clearToken();
      }
    }

    const isDevelopment = process.env.NODE_ENV === 'development' || process.env.UMI_ENV === 'dev';

    if (isDevelopment) {
      const defaultMenuCodes = [
        'operations',
        'customer',
        'report',
        'finance',
        'system',
        'activity',
        'platform',
        'task',
      ];

      return {
        fetchUserInfo,
        currentUser: defaultUser,
        userMenuCodes: defaultMenuCodes,
        mustChangeInitialPassword: false,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }

    return {
      fetchUserInfo,
      currentUser: undefined,
      userMenuCodes: [],
      mustChangeInitialPassword: false,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  } catch (error) {
    console.error('getInitialState 执行失败:', error);

    const isDevelopment = process.env.NODE_ENV === 'development' || process.env.UMI_ENV === 'dev';

    if (isDevelopment) {
      return {
        fetchUserInfo,
        currentUser: defaultUser,
        userMenuCodes: ['operations', 'customer', 'report', 'finance', 'system', 'activity', 'platform', 'task'],
        mustChangeInitialPassword: false,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }

    return {
      fetchUserInfo,
      currentUser: undefined,
      userMenuCodes: [],
      mustChangeInitialPassword: false,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
}
